import { ApiClient } from "./ApiClient";

export type TResetPasswordParams = {
  email: string;
  source: "investor" | "issuer";
  redirect_to?: string;
};

class UserClient extends ApiClient {
  async me() {
    const response = await this.get("/v2/user/me");
    return response.data;
  }

  async register(data: Record<string, any>, type: string = "offering", token: string) {
    const response = await this.post(`/v2/users/register/${type}?token=${token}`, data);
    return response.data;
  }

  async invite(data: Record<string, any>) {
    const response = await this.post("/v2/users/invite", data);
    return response.data;
  }

  async changePassword(payload: Record<string, any>) {
    const response = await this.post("/v2/users/changePassword", payload);
    return response.data;
  }

  async setNewPassword(payload: Record<string, any>) {
    const response = await this.post("/v2/users/setNewPassword", payload);
    return response.data;
  }

  async verifyPasswordToken(payload: Record<string, any>) {
    const response = await this.post("/v2/users/verifyPasswordToken", payload);
    return response.data;
  }

  async update(data: Record<string, any>) {
    const response = await this.post("/v2/user/me", data);
    return response.data;
  }

  async sendPhoneVerification(phone: string): Promise<{ destination: string }> {
    const response = await this.post("/v2/user/me/phone", { phone });
    return response.data;
  }

  async verifyPhone(phone: string, mfa_code: string) {
    const response = await this.post("/v2/user/me/phone/verify", { phone, mfa_code });
    return response.data;
  }

  async getById(userId: string) {
    const response = await this.get(`/v2/user/${userId}`);
    return response.data;
  }

  async updateById(userId: string, data: Record<string, any>) {
    const response = await this.put(`/v2/user/${userId}`, data);
    return response.data;
  }

  async registered(email: string) {
    const response = await this.post("/v2/users/user", {
      email: email,
    });
    return response.data;
  }

  async invitation(type: string, data: object) {
    const response = await this.post(`/v2/users/invitation/${type.toLowerCase()}`, data);
    return response.data;
  }

  async sendResetPasswordEmail(data: TResetPasswordParams) {
    const response = await this.post("/v2/users/auth/reset/", data);
    return response.data;
  }

  async removeIssuerUser(data: Record<string, any>) {
    const response = await this.delete("/v2/users/delete-user-role", { data });
    return response.data;
  }

  async getProfiles() {
    const response = await this.get("/v2/user/profiles");
    return response.data;
  }

  async createSavedProfileFromVault(vaultId: string) {
    const response = await this.post(`/v2/user/profiles/${vaultId}`);
    return response.data;
  }

  async updateSavedProfileFromVault(vaultId: string) {
    const response = await this.put(`/v2/user/profiles/${vaultId}`);
    return response.data;
  }

  async checkSavedProfileExists(accountType: string): Promise<{ exists: boolean }> {
    const response = await this.get(`/v2/user/profiles/${accountType}/exists`);
    return response.data;
  }

  async getSavedProfile(profileId: string) {
    const response = await this.get(`/v2/user/profiles/${profileId}`);
    return response.data;
  }

  async updateSavedProfile(profileId: string, data: Record<string, any>) {
    const response = await this.patch(`/v2/user/profiles/${profileId}`, data);
    return response.data;
  }
  async deleteSavedProfile(profileId: string) {
    const response = await this.delete(`/v2/user/profiles/${profileId}`);
    return response.data;
  }

  async getSavedProfileSigners(profileId: string) {
    const response = await this.get(`/v2/user/profiles/${profileId}/signers`);
    return response.data;
  }

  async getSavedProfileSigner(profileId: string, signerId: string) {
    const response = await this.get(`/v2/user/profiles/${profileId}/signers/${signerId}`);
    return response.data;
  }

  async updateSavedProfileSigner(profileId: string, signerId: string, data: Record<string, any>) {
    const response = await this.patch(`/v2/user/profiles/${profileId}/signers/${signerId}`, data);
    return response.data;
  }

  async deleteSavedProfileSigner(profileId: string, signerId: string) {
    const response = await this.delete(`/v2/user/profiles/${profileId}/signers/${signerId}`);
    return response.data;
  }

  async getSavedProfileFiles(profileId: string) {
    const response = await this.get(`/v2/user/profiles/${profileId}/files`);
    return response.data;
  }

  async storeSavedProfileFile(profileId: string, data: Record<string, any>) {
    const response = await this.post(`/v2/user/profiles/${profileId}/files`, data);
    return response.data;
  }

  async updateSavedProfileFile(profileId: string, fileId: string, data: Record<string, any>) {
    const response = await this.patch(`/v2/user/profiles/${profileId}/files/${fileId}`, data);
    return response.data;
  }

  async deleteSavedProfileFile(profileId: string, fileId: string) {
    const response = await this.delete(`/v2/user/profiles/${profileId}/files/${fileId}`);
    return response.data;
  }

  async getSavedProfilePresignedUrl(profileId: string, filename: string, fileExtension: string) {
    const response = await this.post(`/v2/user/profiles/${profileId}/files/presigned-url`, {
      filename,
      fileExtension,
    });
    return response.data;
  }
}

export default UserClient;
